import styled, { css } from 'styled-components';
import { EMPTY, type ListFieldFormatting } from '@ardoq/api-types';
import { FULL_TRANSPARENT, type HexInputProps } from '@ardoq/forms';
import { FlexBox } from '@ardoq/layout';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { text2 } from '@ardoq/typography';
import { colors, space } from '@ardoq/design-tokens';
import { FormattingColorPicker } from '../../FormattingColorPicker';
import { TextOverflow, WithPopover } from '@ardoq/popovers';
import { ValueWarningMessage } from './ValueWarningMessage';
import { t } from '@ardoq/i18n';
import { useFormattingContext } from '../useFormattingContext';

const colorPickerButtonStyle = css`
  flex: 1;
  border-radius: 4px;
`;

const iconStyle = css`
  align-self: center;
  cursor: pointer;
  margin: 8px;
  color: ${colors.iconAction};
`;

const ItemLabel = styled(TextOverflow)`
  ${text2};
  text-align: left;
  padding: 3px 3px 3px 0;
  overflow-wrap: anywhere;
`;

const Container = styled(FlexBox)<{ $hasWarning?: boolean }>(
  ({ $hasWarning }) => css`
    ${text2};
    min-height: 34px;
    > button {
      ${colorPickerButtonStyle};
      ${
        $hasWarning &&
        css`
          box-shadow: 0 0 0 1px ${colors.borderWarning};
          &:hover {
            box-shadow: 0 0 0 1px ${colors.borderWarning};
          }
        `
      };
      pointer-events: revert;
    }
    > .visibility-icon {
      ${iconStyle}
    }
  `
);

const PickerContent = styled(FlexBox)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

type ListFieldFormattingRuleColorRowProperties = {
  fieldValue: string;
  listFieldFormatting: ListFieldFormatting;
  onColorValueChange: HexInputProps['onValueChange'];
  onIsDisabledToggle: VoidFunction;
  colorWarning?: string;
  colorPaletteAlignment?: 'left' | 'right';
};
/**
 * A row representing one value in a List field.
 *
 * Inside: A color picker, a label, and an isDisabled toggle.
 */
const ListFieldFormattingRuleColorRow = ({
  fieldValue,
  listFieldFormatting,
  onColorValueChange,
  onIsDisabledToggle,
  colorWarning,
  colorPaletteAlignment,
}: ListFieldFormattingRuleColorRowProperties) => {
  const { i18nLocale } = useFormattingContext();
  const isDisabled = Boolean(listFieldFormatting[fieldValue]?.isDisabled);
  const color = isDisabled
    ? colors.grey300
    : listFieldFormatting[fieldValue]?.color || FULL_TRANSPARENT;

  const popover = isDisabled
    ? t(
        i18nLocale,
        'perspectives:listFieldFormattingRuleColorRow.disabledPopover',
        "This color can't be changed while it's hidden. Make it visible first."
      )
    : fieldValue === EMPTY
      ? t(
          i18nLocale,
          'perspectives:listFieldFormattingRuleColorRow.emptyValuePopover',
          'Components with no value for this field will show this color. You can change the color or hide it.'
        )
      : undefined;
  return (
    <FlexBox flexDirection="column">
      <Container gap="small" $hasWarning={Boolean(colorWarning)}>
        <FormattingColorPicker
          dataTestId="formatting-formatting-rule-color-picker"
          value={color}
          onValueChange={onColorValueChange}
          isNewConditionalFormatting
          isDisabled={isDisabled}
          variant="swatch"
          colorPaletteAlignment={colorPaletteAlignment}
        >
          <WithPopover content={popover}>
            <PickerContent>
              <FlexBox align="center" gap="xsmall">
                <Icon iconName={color as IconName} />
                <ItemLabel lineClamp={3}>{fieldValue}</ItemLabel>
              </FlexBox>
              {colorWarning && (
                <Icon
                  iconName={IconName.WARNING}
                  iconSize={IconSize.LARGE}
                  style={{ color: colors.iconWarning }}
                />
              )}
            </PickerContent>
          </WithPopover>
        </FormattingColorPicker>
        <Icon
          className="visibility-icon"
          iconSize={IconSize.MEDIUM}
          iconName={isDisabled ? IconName.VISIBILITY_OFF : IconName.VISIBILITY}
          deprecated_onClick={onIsDisabledToggle}
        />
      </Container>
      <ValueWarningMessage message={colorWarning} marginBottom={space.xsmall} />
    </FlexBox>
  );
};

export { ListFieldFormattingRuleColorRow };
